// App-wide wall for an applicant whose join request denies them the product.
//
// Mirrors the backend's own gate (`require_trial_not_exhausted` refuses `pending` and
// `locked_out`), so the two can't disagree about who is blocked. This one exists so a
// blocked applicant gets an explanation instead of a 403 from whatever page they landed
// on; the backend one is what actually enforces it.
//
// `rejected` also walls the applicant off — a turned-down request shouldn't leave them
// free to keep using their placeholder trial workspace as if nothing happened. The wall
// doubles as the only place they'll see the 7-day-cooldown "Send a new request" button.
//
// `approved_trial` deliberately does not block — that person is using the product on
// trial while they wait, and gets ApprovedTrialBanner instead.
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import JoinRequestStatusPage from './JoinRequestStatusPage';
import { useMyJoinRequest } from './useMyJoinRequest';
import { POLICY_PAGE_PATH_LIST } from '../policy/policyPaths';

interface Props {
    children: React.ReactNode;
}

/** Paths that must render before we know anything about a join request: everything
 *  unauthenticated, plus the flows a blocked applicant still has to be able to finish
 *  (logging out, accepting the policy, buying their own subscription). Kept in step with
 *  FTUEGuard's list, which exists for the same reason. */
const SKIP_PATHS = new Set([
    '/login',
    '/signup',
    '/pricing',
    '/forgot-password',
    '/reset-password',
    '/sso',
    '/session-expired',
    '/marketplace-signup',
    '/no-organization',
    '/payment-success',
    '/getting-started',
    ...POLICY_PAGE_PATH_LIST,
]);

/** The states that deny product access outright. */
const BLOCKING = new Set(['pending', 'locked_out', 'rejected']);

const JoinRequestGuard: React.FC<Props> = ({ children }) => {
    const { currentUser, loading: authLoading } = useAuth();
    const location = useLocation();
    const { request, loading } = useMyJoinRequest();

    const isSkipPath = SKIP_PATHS.has(location.pathname) || location.pathname.startsWith('/auth/magic/');

    if (isSkipPath || !currentUser) {
        return <>{children}</>;
    }

    // Never decide mid-flight. Rendering children first and swapping to the wall a moment
    // later would show a blocked applicant the product, briefly, on every page load.
    if (authLoading || loading) {
        return <LoadingIndicator message="" />;
    }

    if (request.status && BLOCKING.has(request.status)) {
        return <JoinRequestStatusPage />;
    }

    return <>{children}</>;
};

export default JoinRequestGuard;
